import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Pill } from "@/pages/admin/AdminLayout";
import { Card, Field, btn, btnGhost, inputClass } from "@/pages/admin/email/EmailLayout";
import { RichText } from "@/pages/admin/email/RichText";

const AUDIENCE_LABELS = {
  all: "Everyone active",
  tags: "Selected tags",
  manual: "Hand-picked subscribers",
};

export default function EmailCampaigns() {
  const [list, setList] = useState([]);
  const [tags, setTags] = useState([]);
  const [subs, setSubs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [recipients, setRecipients] = useState(null);
  const [preview, setPreview] = useState(null);
  const [canSend, setCanSend] = useState(false);

  const load = useCallback(() => api.get("/admin/email/campaigns").then((r) => setList(r.data)), []);

  useEffect(() => {
    load();
    api
      .get("/admin/email/settings")
      .then((r) => setCanSend(!!r.data.campaign_sending_enabled && !!r.data.marketing_provider));
    api.get("/admin/email/tags").then((r) => setTags(r.data.map((t) => t.name)));
    api
      .get("/admin/email/subscribers", { params: { status: "active", limit: 500 } })
      .then((r) => setSubs(r.data.items));
  }, [load]);

  const create = async () => {
    const { data } = await api.post("/admin/email/campaigns", {
      name: "Untitled campaign",
      subject: "",
      body_html: "<p>Hello,</p>",
      audience: { type: "all", tags: [], subscriber_ids: [] },
    });
    setList((l) => [data, ...l]);
    setEditing(data);
    setRecipients(null);
  };

  const save = async (extra = {}) => {
    const body = {
      name: editing.name,
      subject: editing.subject,
      body_html: editing.body_html,
      audience: editing.audience,
      ...extra,
    };
    const { data } = await api.patch(`/admin/email/campaigns/${editing.id}`, body);
    setEditing(data);
    load();
    toast.success("Campaign saved");
  };

  const countRecipients = async () => {
    await save();
    const { data } = await api.get(`/admin/email/campaigns/${editing.id}/recipients`);
    setRecipients(data);
  };

  const showPreview = async () => {
    await save();
    const { data } = await api.get(`/admin/email/campaigns/${editing.id}/preview`);
    setPreview(data.html);
  };

  const sendTest = async () => {
    await save();
    try {
      const { data } = await api.post(`/admin/email/campaigns/${editing.id}/test`);
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const send = async () => {
    await save();
    if (!canSend) {
      toast.error(
        "Campaign sending is not active yet. Bulk marketing email cannot go through the Emergent managed mail service, which handles transactional messages only, so a marketing email provider must be connected in Email Settings first. This campaign is saved and ready for that moment.",
        { duration: 14000 }
      );
      return;
    }
    const count = recipients?.count;
    if (
      !window.confirm(
        `Send "${editing.subject || editing.name}" to ${count ?? "the selected"} subscriber(s)? This cannot be undone.`
      )
    )
      return;
    try {
      await api.post(`/admin/email/campaigns/${editing.id}/send?confirm=true`);
      toast.success("Campaign sent");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail), { duration: 12000 });
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    await api.delete(`/admin/email/campaigns/${c.id}`);
    if (editing?.id === c.id) setEditing(null);
    load();
  };

  const setAudience = (patch) =>
    setEditing((c) => ({ ...c, audience: { ...c.audience, ...patch } }));

  if (!editing)
    return (
      <div className="space-y-6" data-testid="email-campaigns">
        <button onClick={create} className={btn} data-testid="campaign-create">
          New campaign
        </button>
        <div className="bg-white border border-line overflow-x-auto" data-testid="campaigns-table">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-bone border-b border-line">
                {["Campaign", "Subject", "Audience", "Status", "Sent", ""].map((h) => (
                  <th
                    key={h || "a"}
                    className="px-4 py-3 text-[9.5px] tracking-[0.16em] uppercase text-[#6E6A63] font-medium"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-[13px] font-light text-[#6E6A63]">
                    No campaigns yet.
                  </td>
                </tr>
              )}
              {list.map((c) => (
                <tr key={c.id} className="border-b border-line/70" data-testid={`campaign-row-${c.id}`}>
                  <td className="px-4 py-3.5 text-[13.5px] text-charcoal">{c.name}</td>
                  <td className="px-4 py-3.5 text-[13px] font-light text-[#5A5A5A]">
                    {c.subject || "—"}
                  </td>
                  <td className="px-4 py-3.5 text-[12.5px] font-light text-[#5A5A5A]">
                    {AUDIENCE_LABELS[c.audience?.type] || "—"}
                    {c.audience?.type === "tags" ? `: ${(c.audience.tags || []).join(", ")}` : ""}
                  </td>
                  <td className="px-4 py-3.5">
                    <Pill tone={c.status === "SENT" ? "green" : "yellow"}>{c.status}</Pill>
                  </td>
                  <td className="px-4 py-3.5 text-[12px] font-light text-[#6E6A63]">
                    {c.sent_at ? String(c.sent_at).slice(0, 10) : "—"}
                  </td>
                  <td className="px-4 py-3.5 text-right whitespace-nowrap">
                    <button
                      onClick={() => {
                        setEditing(c);
                        setRecipients(null);
                        setPreview(null);
                      }}
                      data-testid={`campaign-open-${c.id}`}
                      className="text-[11px] tracking-[0.12em] uppercase text-sage hover:text-sage-dark mr-4"
                    >
                      Open
                    </button>
                    <button
                      onClick={() => remove(c)}
                      data-testid={`campaign-delete-${c.id}`}
                      className="text-[11px] tracking-[0.12em] uppercase text-[#991B1B]/70 hover:text-[#991B1B]"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );

  return (
    <div className="space-y-6" data-testid="campaign-editor">
      <button onClick={() => setEditing(null)} className={btnGhost} data-testid="campaign-back">
        Back to campaigns
      </button>

      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-8 items-start">
        <Card title="Content" testid="campaign-content">
          <Field label="Campaign name">
            <input
              value={editing.name}
              onChange={(e) => setEditing((c) => ({ ...c, name: e.target.value }))}
              data-testid="campaign-name"
              className={inputClass}
            />
          </Field>
          <Field label="Email subject">
            <input
              value={editing.subject || ""}
              onChange={(e) => setEditing((c) => ({ ...c, subject: e.target.value }))}
              data-testid="campaign-subject"
              className={inputClass}
            />
          </Field>
          <div className="text-[11px] tracking-[0.14em] uppercase text-[#6E6A63] mb-2">Message</div>
          <RichText
            value={editing.body_html}
            testid={`campaign-body-${editing.id}`}
            onChange={(html) => setEditing((c) => ({ ...c, body_html: html }))}
          />
          <div className="flex flex-wrap gap-3 mt-5">
            <button onClick={() => save()} className={btn} data-testid="campaign-save">
              Save draft
            </button>
            <button onClick={showPreview} className={btnGhost} data-testid="campaign-preview">
              Preview
            </button>
            <button onClick={sendTest} className={btnGhost} data-testid="campaign-test">
              Send test to me
            </button>
          </div>
        </Card>

        <div className="space-y-8">
          <Card title="Recipients" testid="campaign-audience">
            <Field label="Send to">
              <select
                value={editing.audience?.type || "all"}
                onChange={(e) => setAudience({ type: e.target.value })}
                data-testid="campaign-audience-type"
                className={inputClass}
              >
                <option value="all">Everyone active</option>
                <option value="tags">Selected tags</option>
                <option value="manual">Hand-picked subscribers</option>
              </select>
            </Field>

            {editing.audience?.type === "tags" && (
              <div className="flex flex-wrap gap-2 mb-5">
                {tags.map((t) => {
                  const on = (editing.audience.tags || []).includes(t);
                  return (
                    <button
                      key={t}
                      onClick={() =>
                        setAudience({
                          tags: on
                            ? editing.audience.tags.filter((x) => x !== t)
                            : [...(editing.audience.tags || []), t],
                        })
                      }
                      data-testid={`campaign-tag-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`px-3 py-1.5 text-[12px] border transition-colors ${
                        on ? "bg-sage border-sage text-white" : "border-line text-[#5A5A5A]"
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            )}

            {editing.audience?.type === "manual" && (
              <div className="max-h-[260px] overflow-y-auto border border-line mb-5">
                {subs.map((s) => {
                  const on = (editing.audience.subscriber_ids || []).includes(s.id);
                  return (
                    <label
                      key={s.id}
                      className="flex items-center gap-2.5 px-3 py-2 border-b border-line/70 last:border-0 text-[13px] font-light text-[#5A5A5A]"
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() =>
                          setAudience({
                            subscriber_ids: on
                              ? editing.audience.subscriber_ids.filter((x) => x !== s.id)
                              : [...(editing.audience.subscriber_ids || []), s.id],
                          })
                        }
                        data-testid={`campaign-recipient-${s.email}`}
                        className="h-4 w-4 accent-[#7a8b73]"
                      />
                      {s.email}
                    </label>
                  );
                })}
                {subs.length === 0 && (
                  <p className="px-3 py-3 text-[12.5px] font-light text-[#8A867F]">
                    No confirmed subscribers yet.
                  </p>
                )}
              </div>
            )}

            <button onClick={countRecipients} className={btnGhost} data-testid="campaign-count">
              Count recipients
            </button>
            {recipients && (
              <p className="text-[13.5px] text-charcoal mt-4" data-testid="campaign-recipient-count">
                {recipients.count} recipient(s). {recipients.suppressed_excluded} suppressed contact(s)
                excluded.
              </p>
            )}
          </Card>

          <Card title="Send" testid="campaign-send-card">
            <p className="text-[12.5px] font-light text-[#8A867F] leading-relaxed mb-4">
              A campaign is only ever sent when you confirm it here. Bulk sending stays disabled
              until a marketing email provider is connected in Email Settings.
            </p>
            <button onClick={send} className={btn} data-testid="campaign-send">
              Send campaign
            </button>
          </Card>
        </div>
      </div>

      {preview && (
        <Card title="Preview" testid="campaign-preview-panel">
          <iframe
            title="Campaign preview"
            srcDoc={preview}
            className="w-full h-[640px] border border-line bg-white"
          />
          <button onClick={() => setPreview(null)} className={`${btnGhost} mt-5`}>
            Close preview
          </button>
        </Card>
      )}
    </div>
  );
}
